/**
 * RÉFÉRENTIELS → MOYENS DE RÈGLEMENT — la liste que la caisse offre à l'encaissement.
 *
 * POURQUOI CET ONGLET EXISTE. Le canal `payment_method` descend depuis toujours et le producteur sait
 * l'écrire, mais AUCUN écran du siège ne le posait : un moyen ne pouvait venir que de la reprise ou du
 * semis de la Console. Mesuré par `scripts/audit-descente-bo.mjs` — canal ✓, producteur ✓, surface ✗.
 *
 * ⚠️ UN MOYEN QUI A ENCAISSÉ NE SE SUPPRIME JAMAIS, et c'est ce qui dessine cet écran. Un règlement
 * scellé est append-only (NF525) : rien ne le reclassera. Le tombstone retirerait le libellé de toute
 * la flotte alors que des pièces signées portent ce code pour toujours — Z, duplicata et exports
 * comptables montreraient un code que plus rien n'explique. La corbeille n'est donc OFFERTE que sur
 * une ligne qui n'a jamais encaissé ; ailleurs, l'écran propose la désactivation et DIT pourquoi.
 * Offrir un bouton que le serveur refuserait serait une option offerte non implémentée (§23.0).
 *
 * ⚠️ LES INACTIFS RESTENT AFFICHÉS. Ils n'apparaissent plus à l'encaissement mais expliquent encore
 * l'historique, et il faut pouvoir les réactiver : les cacher les rendrait introuvables.
 *
 * ⚠️ L'ORDRE EST UNE DONNÉE, et il descend. Les flèches écrivent `sortOrder` — c'est lui qui décide de
 * l'ordre des tuiles sous le doigt du caissier. La Console ne le propage jamais (SA-10.1) : le siège
 * pose la graine, le magasin l'adapte.
 */
import { useState } from 'react';
import { showToast, Trans } from '@orgazone/ui-web';
import { api, type ReferentialPaymentMethod } from '../api';
import { fieldLabel } from '../vocabulary';
import { key, t, type Translatable } from '../i18n';

/**
 * LES CODES UNTDID 4461 OFFERTS — liste FERMÉE, et sa limite est écrite ici.
 *
 * ⚠️ POURQUOI UNE LISTE PLUTÔT QU'UN NOMBRE LIBRE. Ce code part dans les factures Factur-X et dans
 * l'export comptable ; une faute de frappe y produit un code invalide que RIEN ne signale — ni à la
 * saisie, ni au rendu, seulement chez le destinataire. Le champ libre déplace donc l'erreur là où
 * personne ne la voit.
 *
 * ⚠️ CE NE SONT PAS TOUS LES CODES DU STANDARD, et c'est assumé : ce sont les cinq que le schéma
 * fiscal documente (`fiscal.ts`, `method_4461`) et que la plateforme émet. Un sixième besoin est une
 * LIGNE à ajouter ici — pas une raison de rouvrir la saisie libre.
 */
const CODES_4461: { readonly v: number; readonly l: Translatable }[] = [
  { v: 10, l: key('Espèces') },
  { v: 20, l: key('Chèque') },
  { v: 30, l: key('Virement') },
  { v: 48, l: key('Carte bancaire') },
  { v: 1, l: key('Autre instrument') },
];

/** Le sens d'un code 4461, ou son numéro nu s'il vient d'ailleurs (reprise, Console). */
function sens4461(v: number | null): string | null {
  if (v == null) return null;
  const connu = CODES_4461.find((c) => c.v === v);
  // ⚠️ UN CODE INCONNU S'AFFICHE QUAND MÊME : la reprise legacy et la Console peuvent en poser
  //    d'autres, et le taire ferait croire que la ligne n'en porte aucun.
  return connu ? t(connu.l) : String(v);
}

export function OngletReglements({ jeu, failure, reload }: {
  jeu: ReferentialPaymentMethod[] | null; failure: string | null; reload: () => void;
}) {
  const [composeur, setComposeur] = useState(false);
  const [code, setCode] = useState('');
  const [libelle, setLibelle] = useState('');
  const [u4461, setU4461] = useState<number | null>(null);
  const [armee, setArmee] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const pret = !!code.trim() && !!libelle.trim();

  const creer = async () => {
    setEnCours(true);
    try {
      // ⚠️ CRÉER SEULEMENT : « cb » quand « CB » existe est REFUSÉ par le serveur, qui nomme la ligne existante —
      //    l'écriture ordinaire la réécrivait en silence (libellé, rang, réactivation, nature 4461 effacée).
      await api.createMasterDataOnly('payment_method', {
        code: code.trim(), label: libelle.trim(), method4461: u4461, isActive: true,
        // ⚠️ EN QUEUE, jamais en tête : un moyen neuf ne doit pas passer devant ceux que le caissier
        //    a sous le doigt depuis des mois. L'ordre se règle ensuite, aux flèches.
        sortOrder: (jeu?.length ?? 0) + 1,
      }, `pm:create:${code.trim()}`);
      showToast(t('« {v} » créé — il descendra aux caisses à la prochaine synchronisation.', { v: libelle.trim() }));
      setComposeur(false); setCode(''); setLibelle(''); setU4461(null);
      reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  const basculer = async (m: ReferentialPaymentMethod) => {
    setEnCours(true);
    try {
      await api.createReferentielEntry('payment_method', { code: m.code, isActive: !m.isActive },
        `pm:active:${m.code}:${m.isActive ? 'off' : 'on'}`);
      showToast(m.isActive
        ? t('« {v} » désactivé — il n’est plus proposé à l’encaissement, l’historique le garde.', { v: m.label })
        : t('« {v} » réactivé — il redevient proposé à l’encaissement.', { v: m.label }));
      reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  const deplacer = async (i: number, vers: number) => {
    if (!jeu || vers < 0 || vers >= jeu.length) return;
    const a = jeu[i]!; const b = jeu[vers]!;
    setEnCours(true);
    try {
      // ⚠️ LES DEUX LIGNES PARTENT ENSEMBLE — le producteur écrit un lot en TOUT-OU-RIEN. Deux appels
      //    séparés pourraient n'en graver qu'un, et deux moyens porteraient alors le même rang.
      await api.writeMasterData('payment_method', [
        { code: a.code, sortOrder: b.sortOrder },
        { code: b.code, sortOrder: a.sortOrder },
      ], `pm:order:${a.code}:${b.code}`);
      reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  const supprimer = async (m: ReferentialPaymentMethod) => {
    setEnCours(true);
    try {
      await api.deleteReferentielEntry('payment_method', { code: m.code }, `pm:delete:${m.code}`);
      showToast(t('« {v} » supprimé — il disparaîtra des caisses à la prochaine synchronisation.', { v: m.label }));
      setArmee(null);
      reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  if (failure) {
    return (
      <p role="alert" style={{ margin: 0, padding: '18px 2px', color: 'var(--danger-ink)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
        {t('Le référentiel n’a pas pu être lu : {failure}. Ce n’est pas « aucun moyen de règlement ».', { failure })}</p>
    );
  }
  if (!jeu) return <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture du référentiel…')}</p>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Moyens de règlement')}</div>
          <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: 'var(--ink-500)' }}>
            {t('Ce que la caisse propose à l’encaissement, dans cet ordre. Un moyen qui a déjà encaissé ne se supprime plus : il se désactive.')}
          </p>
        </div>
        <button type="button" onClick={() => setComposeur(true)}
          style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-lead)', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          {t('+ Nouveau moyen')}
        </button>
      </div>

      {composeur ? (
        <div style={{ border: '1px solid var(--teal-300)', background: 'var(--teal-100)', borderRadius: 'var(--r-md)', padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ width: 132 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Code')}</span>
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t('CB, ESP, CHQ…')}
                style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontFamily: 'var(--font-mono)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }} />
            </label>
            <label style={{ flex: 1, minWidth: 190 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{fieldLabel('name')}</span>
              <input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder={t('Carte bancaire, Espèces…')}
                style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }} />
            </label>
            <label style={{ width: 186 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Nature (facture électronique)')}</span>
              <select value={u4461 == null ? '' : String(u4461)}
                onChange={(e) => setU4461(e.target.value === '' ? null : Number(e.target.value))}
                style={{ width: '100%', height: 40, padding: '0 10px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}>
                <option value="">{t('Non précisée')}</option>
                {CODES_4461.map((c) => <option key={c.v} value={String(c.v)}>{t(c.l)}</option>)}
              </select>
            </label>
          </div>
          <p style={{ margin: '10px 0 0', fontSize: '12px', color: 'var(--teal-800)', lineHeight: 1.5 }}>
            {t('Le code identifie le moyen partout — sur les tickets, dans les Z et dans l’export comptable. « cb » et « CB » désignent la même ligne.')}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" disabled={!pret || enCours} onClick={() => void creer()}
              style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: pret ? 'var(--teal-800)' : 'var(--ink-300)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: pret ? 'pointer' : 'default' }}>
              {t('Créer le moyen')}
            </button>
            <button type="button" onClick={() => { setComposeur(false); setCode(''); setLibelle(''); setU4461(null); }}
              style={{ height: 38, padding: '0 13px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: 'pointer' }}>
              {t('Annuler')}
            </button>
          </div>
        </div>
      ) : null}

      {jeu.length === 0 ? (
        <div style={{ padding: '34px 20px', textAlign: 'center', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-md)' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun moyen de règlement')}</div>
          <p style={{ margin: '4px auto 0', fontSize: '12.5px', color: 'var(--ink-500)', maxWidth: 420, lineHeight: 1.55 }}>
            {t('Tant que la liste est vide, la caisse n’a rien à proposer à l’encaissement. Créez au moins les espèces et la carte bancaire.')}
          </p>
        </div>
      ) : (
        <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          {jeu.map((m, i) => (
            <div key={m.code}>
              <div style={{ display: 'grid', gridTemplateColumns: '92px minmax(140px,1fr) minmax(0,140px) 92px 58px 34px', gap: 12, padding: '12px 16px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)' }}>
                <span style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{m.code}</span>
                <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: m.isActive ? 'var(--ink-900)' : 'var(--ink-500)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.label}</span>
                <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sens4461(m.method4461) ?? ''}</span>
                <button type="button" disabled={enCours} onClick={() => void basculer(m)}
                  title={m.isActive ? t('Ne plus proposer à l’encaissement') : t('Proposer à nouveau à l’encaissement')}
                  style={{ justifySelf: 'start', height: 24, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-pill)', background: m.isActive ? 'var(--teal-100)' : 'var(--ink-100)', color: m.isActive ? 'var(--teal-800)' : 'var(--ink-600)', fontSize: 'var(--t-micro)', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {m.isActive ? t('Actif') : t('Inactif')}
                </button>
                <span style={{ display: 'flex', gap: 2 }}>
                  <button type="button" disabled={i === 0 || enCours} aria-label={t('Monter {label}', { label: m.label })}
                    onClick={() => void deplacer(i, i - 1)}
                    style={{ width: 26, height: 26, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: i === 0 ? 'var(--ink-300)' : 'var(--ink-500)', cursor: i === 0 ? 'default' : 'pointer', padding: 0 }}>↑</button>
                  <button type="button" disabled={i === jeu.length - 1 || enCours} aria-label={t('Descendre {label}', { label: m.label })}
                    onClick={() => void deplacer(i, i + 1)}
                    style={{ width: 26, height: 26, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: i === jeu.length - 1 ? 'var(--ink-300)' : 'var(--ink-500)', cursor: i === jeu.length - 1 ? 'default' : 'pointer', padding: 0 }}>↓</button>
                </span>
                <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {/* ⚠️ LA CORBEILLE N'EXISTE QUE SUR UNE LIGNE QUI N'A JAMAIS ENCAISSÉ. Ailleurs, le
                      serveur refuserait — et un bouton qui échoue toujours est un bouton qui ment. */}
                  {m.sealedPayments === 0 && armee !== m.code ? (
                    <button type="button" aria-label={t('Supprimer {label}', { label: m.label })} title={t('Supprimer')} onClick={() => setArmee(m.code)}
                      style={{ width: 30, height: 30, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" /></svg>
                    </button>
                  ) : null}
                </span>
              </div>
              {m.sealedPayments > 0 ? (
                <div style={{ padding: '0 16px 10px', marginTop: -4, fontSize: '12px', color: 'var(--ink-500)', lineHeight: 1.5 }}>
                  {t('{n} règlement scellé porte ce code : il ne se supprime plus, seulement se désactive.', { n: m.sealedPayments })}
                </div>
              ) : null}
              {armee === m.code ? (
                // ⚠️ EN LIGNE, sous la ligne visée — jamais un confirm natif.
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '10px 16px', background: 'var(--amber-bg)', borderBottom: '1px solid var(--ink-100)' }}>
                  <span style={{ flex: 1, minWidth: 200, fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.5 }}>
                    <Trans k='Supprimer « {label} » ? Il disparaîtra de <1>toutes les caisses</1>. Aucun règlement ne le porte, l’historique reste intact.'
                      vars={{ label: m.label }} parts={[(x) => <strong>{x}</strong>]} /></span>
                  <button type="button" disabled={enCours} onClick={() => void supprimer(m)}
                    style={{ height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--rose-fg)', color: '#fff', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>
                    {t('Supprimer')}
                  </button>
                  <button type="button" onClick={() => setArmee(null)}
                    style={{ height: 32, padding: '0 13px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
                    {t('Garder')}
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
        {t('Ce que vous posez ici descend aux caisses à la prochaine synchronisation. L’ordre vous appartient : le siège pose la graine, le magasin l’adapte.')}
      </div>
    </div>
  );
}
